import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import { useLocation } from "react-router-dom";
import { Content2Xl } from "components/misc/Layouts.js";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";

// Conteneur pour le fond de l'en-tête.
// Rendu hors du conteneur de page (App.js), donc padding horizontal propre, sans marge négative
// (les -mx-8 du template faisaient déborder la page de 32 px et coupaient « Contact » à droite).
const PrimaryBackgroundContainer = tw.div`px-8 text-gray-900`;

// Style de l'en-tête avec largeur pleine et marges négatives
const Header = tw.header`
  flex justify-between items-center
  w-full max-w-none -mt-8 py-8
`;

export const NavLinks = tw.div`inline-block flex space-x-6 lg:space-x-8`;

export const NavLink = styled.a`
  ${tw`text-lg my-2 lg:text-base lg:my-0 font-semibold tracking-wide transition duration-300`}
  ${tw`pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500`}
  &.active {
    ${tw`border-primary-500 text-primary-500`}
  }
`;

export const PrimaryLink = tw(NavLink)`
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-xl lg:text-2xl ml-0!`};

  img {
    ${tw`w-12 lg:w-16 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;

export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

export default ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "lg" }) => {

  // Lien courant souligné en violet (aria-current pour les lecteurs d'écran).
  const { pathname } = useLocation();
  const isCurrent = href => (href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`));
  const navItem = (href, label) => (
    <NavLink key={href} href={href} aria-current={isCurrent(href) ? "page" : undefined} className={isCurrent(href) ? "active" : undefined}>
      {label}
    </NavLink>
  );

  const defaultLinks = [
    <NavLinks key={1}>
      {navItem("/", "Accueil")}
      {navItem("/blog", "Blog")}
      {navItem("/about", "À propos")}
      {navItem("/contact", "Contact")}
    </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="logo" />
      Besançon
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <PrimaryBackgroundContainer>
      <Content2Xl>
        <Header className={className || "header-light"}>
          <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
            {logoLink}
            {links}
          </DesktopNavLinks>

          <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
            {logoLink}
            <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
              {links}
            </MobileNavLinks>
            <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
              {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
            </NavToggle>
          </MobileNavLinksContainer>
        </Header>
      </Content2Xl>
    </PrimaryBackgroundContainer>
  );
};

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`xl:hidden`,
    desktopNavLinks: tw`xl:flex`,
    mobileNavLinksContainer: tw`xl:hidden`
  }
};
